import {useState} from 'react';
import moment from 'moment';

import {DateRange, DateMoment} from '../../../types';

const useRangePickerData = ({
    selectedDate: selected,
    startDate: start,
    endDate: end
}: DateRange) => {
    const [isPreselection, setIsPreselection] = useState(true);
    const [{startDate, endDate, selectedDate}, setCalendar] = useState<DateRange>({
        selectedDate: selected,
        startDate: start,
        endDate: end
    });

    const sortInterval = ({startDate, endDate}: DateRange) => {
        // Implicitly clone startDate and endDate
        const startInterval: moment.Moment = moment(startDate);
        const endInterval: moment.Moment = moment(endDate);

        if (moment(startInterval).isAfter(endInterval, 'day')) {
            return {arrangedStartOfInterval: endInterval, arrangedEndOfInterval: startInterval};
        } else {
            return {arrangedStartOfInterval: startInterval, arrangedEndOfInterval: endInterval};
        }
    };

    const handleDayChange = (date: DateMoment) => {
        if (isPreselection) {
            setCalendar({startDate: date, endDate: date, selectedDate: date});
            setIsPreselection(false);
            return;
        }
        const isInterval = !moment(startDate).isSame(endDate, 'day');

        if (isInterval) {
            setCalendar({startDate: date, endDate: date, selectedDate: date});
        } else {
            if (moment(date).isAfter(selectedDate, 'day')) {
                setCalendar({startDate: selectedDate, endDate: date, selectedDate: date});
            } else {
                setCalendar({startDate: date, endDate: selectedDate, selectedDate: date});
            }
        }
    };

    const handleWeekChange = (date: DateMoment) => {
        if (isPreselection) {
            setIsPreselection(false);
        }

        const isInterval = !moment(startDate).isSame(endDate);
        const startWeekInitialDate = moment(startDate).startOf('week');
        const endWeekInitialDate = moment(endDate).endOf('week');

        const {arrangedStartOfInterval, arrangedEndOfInterval} = sortInterval({startDate, endDate});
        const isDateBeforeStart = moment(date).isBefore(arrangedStartOfInterval);
        const isDateAfterEnd = moment(date).isAfter(arrangedEndOfInterval);

        if (isInterval) {
            if (
                !moment(startDate).isSame(startWeekInitialDate) ||
                !moment(endDate).isSame(endWeekInitialDate)
            ) {
                const startDate = moment(date).startOf('week');
                const endDate = moment(date).endOf('week');
                setCalendar({startDate, endDate, selectedDate: date});
                return;
            }

            if (isDateBeforeStart) {
                const startWeekDate = moment(date).startOf('week');
                setCalendar({
                    startDate: startWeekDate,
                    endDate: arrangedEndOfInterval,
                    selectedDate: date
                });
            } else if (isDateAfterEnd) {
                const endWeekDate = moment(date).endOf('week');
                setCalendar({
                    startDate: arrangedStartOfInterval,
                    endDate: endWeekDate,
                    selectedDate: date
                });
            } else {
                const startDate = moment(date).startOf('week');
                const endDate = moment(date).endOf('week');
                setCalendar({startDate, endDate, selectedDate: date});
            }
        } else {
            const startDate = moment(date).startOf('week');
            const endDate = moment(date).endOf('week');
            setCalendar({startDate, endDate, selectedDate: date});
        }
    };

    const handleMonthChange = (date: DateMoment) => {
        if (isPreselection) {
            setIsPreselection(false);
        }

        const isInterval = !moment(startDate).isSame(endDate);

        const startMonthInitialDate = moment(startDate).startOf('month');
        const endMonthInitialDate = moment(endDate).endOf('month');

        const {arrangedStartOfInterval, arrangedEndOfInterval} = sortInterval({startDate, endDate});
        const isDateBeforeStart = moment(date).isBefore(arrangedStartOfInterval);
        const isDateAfterEnd = moment(date).isAfter(arrangedEndOfInterval);

        if (isInterval) {
            if (
                !moment(startDate).isSame(startMonthInitialDate) ||
                !moment(endDate).isSame(endMonthInitialDate)
            ) {
                setCalendar({
                    startDate: startMonthInitialDate,
                    endDate: endMonthInitialDate,
                    selectedDate: date
                });
                return;
            }

            if (isDateBeforeStart) {
                const startMonthDate = moment(date).startOf('month');
                setCalendar({
                    startDate: startMonthDate,
                    endDate: arrangedEndOfInterval,
                    selectedDate: date
                });
            } else if (isDateAfterEnd) {
                const endMonthDate = moment(date).endOf('month');
                setCalendar({
                    startDate: arrangedStartOfInterval,
                    endDate: endMonthDate,
                    selectedDate: date
                });
            } else {
                const startDate = moment(date).startOf('month');
                const endDate = moment(date).endOf('month');
                setCalendar({startDate, endDate, selectedDate: date});
            }
        } else {
            const startDate = moment(date).startOf('month');
            const endDate = moment(date).endOf('month');
            setCalendar({startDate, endDate, selectedDate: date});
        }
    };

    const handleYearMonthChange = date => {
        setCalendar({
            startDate,
            endDate,
            selectedDate: date
        });
    };

    return {
        isPreselection,
        selectedDate,
        startDate,
        endDate,
        setIsPreselection,
        handleDayChange,
        handleWeekChange,
        handleMonthChange,
        handleYearMonthChange,
        setCalendar
    };
};

export default useRangePickerData;
